import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "camera-editor" }
const _hoisted_2 = {
  key: 0,
  class: "camera-basics"
}
const _hoisted_3 = {
  key: 0,
  class: "camera-notice",
  role: "alert"
}
const _hoisted_4 = {
  key: 0,
  class: "camera-notice"
}
const _hoisted_5 = {
  key: 1,
  class: "camera-notice"
}
const _hoisted_6 = {
  class: "motion-tabs",
  role: "group",
  "aria-label": "镜头配置"
}
const _hoisted_7 = ["aria-pressed"]
const _hoisted_8 = ["aria-pressed"]
const _hoisted_9 = {
  key: 2,
  class: "viewpoint-toggle"
}
const _hoisted_10 = ["checked"]
const _hoisted_11 = {
  key: 3,
  class: "viewpoint-empty"
}
const _hoisted_12 = { class: "camera-advanced" }

import { studioSelectTheme } from "../../../studioSelectTheme";
import "../../../../studioSelect.css";
import { computed, ref, type HTMLAttributes } from "vue";
import { NSelect } from "naive-ui";
import { useStylePresets } from "../../../EntityPresetEditor/stylePresets";
import type { PerformanceClip } from "../../types/DialogueNode";
import { CAMERA_CLIP_COMPONENT_TEMPLATE, CAMERA_POSITION_PROPERTIES, CAMERA_ROTATION_PROPERTIES, normalizeCameraProperties } from "../../config/cameraClip";
import { createClipComponent } from "../../config/clipComponentRegistry";
import { createClipPropertyValues } from "../../utils/clipProperties";
import CameraMotionEditor from "./CameraMotionEditor.vue";
import ClipComponentsEditor from "./ClipComponentsEditor.vue";


export default /*@__PURE__*/_defineComponent({
  __name: 'CameraClipEditor',
  props: {
    clip: {}
  },
  setup(__props: any) {

const props = __props;
// The teleported menu belongs to this editor, not the outside-click area.
const clipMenuProps: HTMLAttributes & { "data-clip-editor": string } = { "data-clip-editor": "", class: "studio-select-menu" };
const { options: cameraPresets, ready, error, retry } = useStylePresets("cameras");
const cameraOptions = computed(() => {
  const options = cameraPresets.value.map(item => ({ value: item.value, label: `${item.label} · ${item.value}` }));
  const current = String(values.value.cameraName ?? "");
  if (!options.some(item => item.value === current)) options.push({ value: current, label: current || "未设置" });
  return options;
});
const tab = ref<"position" | "rotation">("position");
const shots = computed(() => props.clip.components.filter(item => item.templateId === "camera.shot"));
// Match the final enabled camera component used by the existing exporter.
const shot = computed(() => [...shots.value].reverse().find(item => item.enabled) ?? shots.value[0]);
const viewpointEnabled = computed(() => shot.value?.cameraViewpointEnabled !== false);
const values = computed(() => createClipPropertyValues(CAMERA_CLIP_COMPONENT_TEMPLATE.properties, normalizeCameraProperties(shot.value?.properties)));
function update(key: string, value: unknown) {
  if (!shot.value) return;
  const id = shot.value.id;
  props.clip.components = props.clip.components.map(item => item.id === id ? { ...item, properties: { ...item.properties, [key]: value } } : item);
}
function addShot() { if (!shots.value.length) props.clip.components = [...props.clip.components, createClipComponent("camera.shot")]; }
function setViewpointEnabled(event: Event) {
  const id = shot.value?.id;
  props.clip.components = props.clip.components.map(item => item.id === id ? { ...item, cameraViewpointEnabled: (event.target as HTMLInputElement).checked } : item);
}
function mode(key: "positionData" | "rotationData") {
  const type = String((values.value[key] as Record<string, unknown>).type || "未设置");
  const definitions = key === "positionData" ? CAMERA_POSITION_PROPERTIES : CAMERA_ROTATION_PROPERTIES;
  return definitions.find(field => field.key === "type")?.options?.find(option => option.value === type)?.label ?? type;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (shot.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("label", null, [
            _cache[5] || (_cache[5] = _createTextVNode("镜头名称", -1)),
            _createVNode(_unref(NSelect), {
              "theme-overrides": _unref(studioSelectTheme),
              value: String(values.value.cameraName ?? ''),
              options: cameraOptions.value,
              disabled: !_unref(ready),
              filterable: "",
              "menu-props": clipMenuProps,
              placeholder: "搜索镜头名称",
              "aria-label": "相机预设",
              "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => (update('cameraName', $event)))
            }, null, 8, ["theme-overrides", "value", "options", "disabled"])
          ]),
          (_unref(error))
            ? (_openBlock(), _createElementBlock("p", _hoisted_3, [
                _createTextVNode(_toDisplayString(_unref(error)) + " ", 1),
                _createElementVNode("button", {
                  type: "button",
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(retry)().catch(() => undefined)))
                }, "重试")
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    (shot.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (!shot.value.enabled)
            ? (_openBlock(), _createElementBlock("p", _hoisted_4, "此镜头参数已停用，可在高级设置中重新启用。"))
            : _createCommentVNode("", true),
          (shots.value.length > 1)
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, "存在多份镜头参数，请在下方高级设置中整理。当前编辑最后一份已启用的参数。"))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _createElementVNode("button", {
              type: "button",
              "aria-pressed": tab.value === 'position',
              onClick: _cache[2] || (_cache[2] = ($event: any) => (tab.value = 'position'))
            }, [
              _cache[6] || (_cache[6] = _createElementVNode("span", null, "相机位置", -1)),
              _createElementVNode("small", null, _toDisplayString(mode('positionData')), 1)
            ], 8, _hoisted_7),
            _createElementVNode("button", {
              type: "button",
              "aria-pressed": tab.value === 'rotation',
              onClick: _cache[3] || (_cache[3] = ($event: any) => (tab.value = 'rotation'))
            }, [
              _cache[7] || (_cache[7] = _createElementVNode("span", null, "视点位置", -1)),
              _createElementVNode("small", null, _toDisplayString(viewpointEnabled.value ? mode('rotationData') : '未配置'), 1)
            ], 8, _hoisted_8)
          ]),
          (tab.value === 'rotation')
            ? (_openBlock(), _createElementBlock("label", _hoisted_9, [
                _createElementVNode("input", {
                  type: "checkbox",
                  checked: viewpointEnabled.value,
                  onChange: setViewpointEnabled
                }, null, 40, _hoisted_10),
                _cache[8] || (_cache[8] = _createTextVNode("配置视点位置", -1)),
                _cache[9] || (_cache[9] = _createElementVNode("small", null, "可选", -1))
              ]))
            : _createCommentVNode("", true),
          (tab.value === 'rotation' && !viewpointEnabled.value)
            ? (_openBlock(), _createElementBlock("p", _hoisted_11, "当前只配置相机位置。勾选后可设置视点的位置与移动方式。"))
            : (_openBlock(), _createBlock(CameraMotionEditor, {
                key: tab.value,
                kind: tab.value,
                "model-value": values.value[tab.value === 'position' ? 'positionData' : 'rotationData'],
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => (update(tab.value === 'position' ? 'positionData' : 'rotationData', $event)))
              }, null, 8, ["kind", "model-value"]))
        ], 64))
      : (_openBlock(), _createElementBlock("button", {
          key: 2,
          type: "button",
          class: "restore-shot",
          onClick: addShot
        }, "＋ 添加镜头参数")),
    _createElementVNode("details", _hoisted_12, [
      _cache[10] || (_cache[10] = _createElementVNode("summary", null, [
        _createTextVNode("高级设置"),
        _createElementVNode("span", null, "组件与自定义属性")
      ], -1)),
      _createVNode(ClipComponentsEditor, { clip: __props.clip }, null, 8, ["clip"])
    ])
  ]))
}
}

})